import type { ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useMe } from '../_app';
import { ReasonAction } from '../../components/reason-action';
import { freshnessOf } from '../../lib/format';
import { Empty, Facts, Mono, Page, PageHeader, Panel, Skeleton, Table, col } from '@bounded/ui/primitives';
import { ErrorNotice, FreshnessLabel } from '@bounded/ui/state';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/accounts/$accountId')({ component: Account });

/** Disabled is an admin action on the platform, not a device state, so it gets a plain outlined label (no verdict colour). */
export function AccountStatus({ status }: { status: 'active' | 'disabled' }) {
  const t = useT();
  return status === 'disabled'
    ? <span className="inline-flex h-6 items-center rounded-full border border-deny/50 px-2 text-sm whitespace-nowrap text-deny">{t.accounts.disabled}</span>
    : <span className="whitespace-nowrap">{t.accounts.active}</span>;
}

function Account() {
  const t = useT(); const m = t.accounts; const f = useFormat();
  const { accountId } = Route.useParams();
  const me = useMe();
  const queryClient = useQueryClient();
  const account = useQuery(q.account(accountId));
  const devices = useQuery(q.accountDevices(accountId));
  if (account.error) return <Page><ErrorNotice error={account.error} onRetry={() => void account.refetch()} /></Page>;
  if (!account.data) return <Page><Skeleton className="h-64" /></Page>;
  const a = account.data.account;
  const disabling = a.status === 'active';
  // Server rules: operator+ can change accounts; only owners can change admin accounts.
  const blocked = !me.permissions.mutate ? t.errorCodes.ADMIN_FORBIDDEN.detail : a.admin_role && !me.permissions.manage_admin_accounts ? m.adminAccount : null;

  return (
    <Page>
      <PageHeader title={a.name} description={<Mono value={a.id} head={40} copy />} />
      <Panel title={m.detailFacts}>
        <div className="grid gap-5 px-4 py-4">
          <Facts items={[
            [t.labels.status, <AccountStatus key="s" status={a.status} />],
            [t.labels.created, f.dateTime(a.created_at)],
            [m.adminRole, a.admin_role ? t.roles[a.admin_role] : t.labels.none],
            ...(a.disabled_at ? [[m.disabledAt, f.dateTime(a.disabled_at)], [m.disabledReason, <span key="r" className="[overflow-wrap:anywhere]">{a.reason}</span>]] as [string, ReactNode][] : []),
          ]} />
          <ReasonAction danger={disabling} label={disabling ? m.disable : m.enable} title={disabling ? m.disableTitle : m.enableTitle}
            consequence={disabling ? m.disableConsequence : m.enableConsequence}
            disabled={!!blocked} note={blocked ?? m.recentLogin}
            payload={{ account: a.id, status: disabling ? 'disabled' : 'active', expected_revision: a.revision }}
            run={(reason, key) => api.setAccountStatus(a.id, { expected_revision: a.revision, status: disabling ? 'disabled' : 'active', reason }, key)}
            onDone={() => Promise.all([queryClient.invalidateQueries({ queryKey: ['account', a.id] }), queryClient.invalidateQueries({ queryKey: ['accounts'] }), queryClient.invalidateQueries({ queryKey: ['audit'] })])} />
        </div>
      </Panel>
      <Panel title={m.devices}>
        {devices.error ? <div className="p-4"><ErrorNotice error={devices.error} onRetry={() => void devices.refetch()} /></div>
          : !devices.data ? <div className="p-4"><Skeleton className="h-20" /></div>
          : !devices.data.items.length ? <Empty title={m.devicesEmpty} />
          : <Table head={[{ label: t.labels.name }, { label: t.devices.lastReport }, { label: t.labels.created, className: col.hideSm }]}>
              {devices.data.items.map((d) => (
                <tr key={d.id}>
                  <td><Link to="/devices/$deviceId" params={{ deviceId: d.id }} className="font-medium hover:underline">{d.name}</Link></td>
                  <td><FreshnessLabel freshness={freshnessOf(d.reported_at, d.revoked_at !== null)} at={d.reported_at} /></td>
                  <td className={`${col.hideSm} text-graphite`}>{f.dateTime(d.created_at)}</td>
                </tr>
              ))}
            </Table>}
      </Panel>
    </Page>
  );
}
